import React, { useEffect, useState } from "react";
import "./giftyourmediums.css";
import { useTranslation } from "react-i18next";

/**
 * "Gift Your Medium" popup.
 *
 * Mirrors the popup on the post-survey thank-you page (verysoul_backend:
 * resources/views/thanks.blade.php, styled by public/assets/css/survey.css and
 * mobile.css) — same card, amount row and outlined payment buttons. The styles live
 * in giftyourmediums.css under `.gift-old-popup`; the gift-* class names are
 * deliberately distinct from the global `.payment-option` / `.bold` classes so
 * nothing else in the app restyles them (and vice versa).
 *
 * Which buttons show is decided by the medium's `is_*_integrated` flags on `activePay`.
 */
export default function GiftYourMediums({ activePay, paymentMethodHandler, setPaymentAmount, paymentstatus }) {
  const { t } = useTranslation("clientSessions");
  const [inputValue, setInputValue] = useState("");

  // Pay It Forward gift: the medium has Pay It Forward on, so the gift goes to the VerySoul
  // Stripe account in USD (decided server-side) and the medium needs no gateway of their own.
  const isPayForwardGift = !!(activePay && activePay.gift_is_pay_forward);
  const suggestedAmount =
    isPayForwardGift && activePay.suggested_gift_amount ? String(activePay.suggested_gift_amount) : "";

  useEffect(() => {
    // Reset on every open; donated sessions pre-fill the suggested amount (still editable).
    setInputValue(suggestedAmount);
    setPaymentAmount(suggestedAmount);
  }, [paymentstatus, activePay]);

  const paymentChangeHandler = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setPaymentAmount(value);
  };

  const currency = isPayForwardGift
    ? activePay.gift_currency || "USD"
    : (activePay && activePay.currency) || "USD";

  const gateways = isPayForwardGift ? [
    {
      key: "stripe",
      enabled: true,
      image: "/integrationimages/stripe.png",
      alt: "Stripe",
      caption: t("giftYourMedium.card"),
    },
  ] : [
    {
      key: "paypal",
      enabled: activePay && activePay.is_paypal_integrated,
      image: "/integrationimages/paypal.png",
      alt: "PayPal",
      caption: t("giftYourMedium.paypalOrCard"),
    },
    {
      key: "stripe",
      enabled: activePay && activePay.is_stripe_integrated,
      image: "/integrationimages/stripe.png",
      alt: "Stripe",
      caption: t("giftYourMedium.card"),
    },
    {
      key: "square",
      enabled: activePay && activePay.is_square_integrated,
      image: "/integrationimages/square-black.jpg",
      alt: "Square",
      caption: t("giftYourMedium.card"),
    },
  ];

  const availableGateways = gateways.filter((gateway) => gateway.enabled);

  // Helps pinpoint "no payment buttons" reports: shows exactly what the appointment row carried.
  useEffect(() => {
    if (activePay && availableGateways.length === 0) {
      console.warn("GiftYourMediums: no payment gateway flags on appointment", {
        id: activePay.id,
        medium_id: activePay.medium_id,
        session_type: activePay.session_type,
        is_pay_forward: activePay.is_pay_forward,
        can_gift_medium: activePay.can_gift_medium,
        payment_gateway: activePay.payment_gateway,
        is_stripe_integrated: activePay.is_stripe_integrated,
        is_paypal_integrated: activePay.is_paypal_integrated,
        is_square_integrated: activePay.is_square_integrated,
      });
    }
  }, [activePay]);

  return (
    <div
      className="modal fade gift-old-popup"
      tabIndex="-1"
      role="dialog"
      id="GiftYourMediums"
      aria-labelledby="giftYourMediumsTitle"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="popp-from">
            <div className="from-popup">
              <h2 className="text-center" id="giftYourMediumsTitle">{t("giftYourMedium.title")}</h2>
              <div className="fild-box d-flex">
                <div className="center-div">
                  <label htmlFor="giftAmount">{t("giftYourMedium.enterAmount")}</label>
                  <input
                    type="number"
                    id="giftAmount"
                    min="1"
                    max="100"
                    className="gift-amount-input"
                    placeholder=""
                    value={inputValue}
                    onChange={paymentChangeHandler}
                  />
                  <span className="gift-bold">{currency}</span>
                </div>
              </div>

              <div className="send-bution text-center">
                <div className="gift-pay-options">
                  {availableGateways.length === 0 && (
                    <span className="gift-bold">
                      {t("giftYourMedium.noPaymentMethod")}
                    </span>
                  )}
                  {availableGateways.map((gateway) => (
                      <div className="gift-pay-option-wrapper" key={gateway.key}>
                        <button
                          type="button"
                          className="gift-pay-option"
                          onClick={() => paymentMethodHandler(gateway.key)}
                        >
                          <img src={gateway.image} alt={gateway.alt} />
                        </button>
                        <span className="gift-bold">{gateway.caption}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
